import React, { useState } from 'react';
import { Github, ExternalLink, Code, Smartphone, Globe, Terminal, Monitor } from 'lucide-react';

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: "E-Commerce Game Store",
      description: "Engineered and deployed a full-stack e-commerce platform for game sales with HTML CSS frontend, Node.js/Express backend, SQL Server database, shopping cart, and secure checkout system.",
      image: "/api/placeholder/600/400",
      technologies: ["HTML", "CSS", "Node.js", "Express", "SQL Server", "JWT"],
      category: "fullstack",
      githubLink: "https://github.com/zaidharissheikh/Game_Store",
      liveLink: "#",
      gradient: "from-blue-500 to-purple-600",
      icon: <Code className="w-6 h-6" />
    },
    {
      id: 2,
      title: "AskGem",
      description: "AskGem is a modern, AI-driven web application designed to help students discover their ideal career path and academic roadmap.",
      image: "/api/placeholder/600/400",
      technologies: ["React", "Express", "Node.js", "Google Gemini AI"],
      category: "fullstack",
      githubLink: "https://github.com/fatima-Sami55/AskGem",
      liveLink: "https://ask-gem.vercel.app/",
      gradient: "from-purple-500 to-pink-600",
      icon: <Terminal className="w-6 h-6" />
    },
    {
      id: 3,
      title: "Twitter UI Clone",
      description: "Built a pixel-perfect, responsive Twitter-like user interface to practice modern design patterns and component architecture using TailwindCSS and HTML.",
      image: "/api/placeholder/600/400",
      technologies: ["TailwindCSS", "JavaScript", "Responsive Design"],
      category: "frontend",
      githubLink: "https://github.com/zaidharissheikh/twitter_ui_clone",
      liveLink: "#",
      gradient: "from-cyan-500 to-blue-600",
      icon: <Smartphone className="w-6 h-6" />
    },
    {
      id: 4,
      title: "Spotify Web App Clone",
      description: "Developed a responsive Spotify-inspired web application with playlist management, functional audio controls (play/pause/seek), and modern UI components.",
      image: "/api/placeholder/600/400",
      technologies: ["HTML", "CSS", "JavaScript", "Web Audio API"],
      category: "frontend", 
      githubLink: "https://github.com/zaidharissheikh/spotify-website-clone",
      liveLink: "#",
      gradient: "from-green-500 to-emerald-600",
      icon: <Globe className="w-6 h-6" />
    },
    {
      id: 5,
      title: "Mini-Instagram CLI",
      description: "Built a command-line Instagram prototype using C++ with Object-Oriented Programming principles and advanced data structures for user management and post handling.",
      image: "/api/placeholder/600/400",
      technologies: ["C++", "OOP", "Data Structures", "STL"],
      category: "backend",
      githubLink: "https://github.com/zaidharissheikh/DSA-Lab-Project",
      liveLink: "#",
      gradient: "from-purple-500 to-pink-600",
      icon: <Terminal className="w-6 h-6" />
    },
    {
      id: 6,
      title: "Minesweeper CLI",
      description: "Created a feature-rich C++ Minesweeper game with multiple difficulty modes, persistent leaderboards, and an intuitive command-line interface.",
      image: "/api/placeholder/600/400",
      technologies: ["C++", "File I/O", "Game Logic", "CLI"],
      category: "backend",
      githubLink: "https://github.com/zaidharissheikh/Minesweeper-cli", 
      liveLink: "#",
      gradient: "from-orange-500 to-red-600",
      icon: <Terminal className="w-6 h-6" />
    }
  ];

  const filters = [
    { key: 'all', label: 'All Projects' },
    { key: 'fullstack', label: 'Full Stack' },
    { key: 'frontend', label: 'Frontend' },
    { key: 'backend', label: 'Backend' }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <section id="projects" className="py-20 bg-gray-800">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Featured <span className="text-cyan-400">Projects</span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-purple-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              A showcase of my technical skills and creative problem-solving
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {filters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeFilter === filter.key
                    ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white shadow-lg shadow-cyan-500/25'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <div 
                key={project.id}
                className="group bg-gray-900/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700 hover:border-gray-600 transition-all duration-300 hover:transform hover:scale-105"
              >
                {/* Project Image */}
                <div className="relative h-48 overflow-hidden">
                  <div className={`absolute inset-0 bg-gradient-to-br ${project.gradient} opacity-90`}></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-white/80 group-hover:scale-110 transition-transform duration-300">
                      {project.icon}
                      <div className="w-24 h-24 mx-auto mt-4 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <span className="text-2xl font-bold">{project.title.charAt(0)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Project Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors duration-300">
                    {project.title}
                  </h3>
                  
                  <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                    {project.description}
                  </p>

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.technologies.slice(0, 3).map((tech, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-gray-800 text-cyan-400 text-xs rounded-full border border-gray-600"
                      >
                        {tech}
                      </span>
                    ))}
                    {project.technologies.length > 3 && (
                      <span className="px-3 py-1 bg-gray-800 text-gray-400 text-xs rounded-full border border-gray-600">
                        +{project.technologies.length - 3}
                      </span>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-4">
                    <a
                      href={project.githubLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-gray-300 hover:text-cyan-400 transition-colors duration-300"
                    >
                      <Github size={18} />
                      <span className="text-sm">Code</span>
                    </a>
                    
                    {project.liveLink !== "#" && (
                      <a
                        href={project.liveLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-gray-300 hover:text-cyan-400 transition-colors duration-300"
                      >
                        <ExternalLink size={18} />
                        <span className="text-sm">Live</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Freelance Websites iframe Section */}
          <div className="mt-20">
            <h3 className="text-3xl font-bold text-white text-center mb-8">
              Freelance <span className="text-cyan-400">Websites</span>
            </h3>
            <p className="text-center text-gray-300 mb-12">
              Experience my freelance work directly - interactive previews of real client websites
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700">
                <h4 className="text-xl font-semibold text-white mb-4">Ibrahim Dairies</h4>
                <div className="aspect-video bg-gray-800 rounded-lg overflow-hidden">
                  <iframe 
                    src="https://ibrahimdairies.pk/"
                    className="w-full h-full border-0 rounded-lg"
                    title="Ibrahim Dairies Website"
                    loading="lazy"
                  />
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-gray-800 text-cyan-400 text-xs rounded-full border border-gray-600">HTML</span>
                    <span className="px-3 py-1 bg-gray-800 text-cyan-400 text-xs rounded-full border border-gray-600">CSS</span>
                    <span className="px-3 py-1 bg-gray-800 text-cyan-400 text-xs rounded-full border border-gray-600">JavaScript</span>
                  </div>
                  <a 
                    href="https://ibrahimdairies.pk/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-cyan-400 hover:text-cyan-300 transition-colors duration-300 text-sm font-medium"
                  >
                    Visit Live Site →
                  </a>
                </div>
              </div>
              
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-3 border border-gray-700">
                <h4 className="text-xl font-semibold text-white mb-4">WeaveExperts</h4>
                <div className="aspect-video bg-gray-800 rounded-lg overflow-hidden">
                  <iframe 
                    src="https://weaveexperts.com/"
                    className="w-full h-full border-0 rounded-lg"
                    title="WeaveExperts Website"
                    loading="lazy"
                  />
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-gray-800 text-cyan-400 text-xs rounded-full border border-gray-600">React</span>
                    <span className="px-3 py-1 bg-gray-800 text-cyan-400 text-xs rounded-full border border-gray-600">SEO</span>
                  </div>
                  <a 
                    href="https://weaveexperts.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-cyan-400 hover:text-cyan-300 transition-colors duration-300 text-sm font-medium"
                  >
                    Visit Live Site →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
